import { useMemo } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import type { AgencyFilter } from '@modules/_shared/hooks/useAgencyFilter';
import { useStateByCoordinates } from '@modules/_shared/hooks/useStateByCoordinates';
import {
  buildCountryWhereClause,
  buildRadiusWhereClause,
} from '@modules/_shared/utils/countryQueryHelpers';
import {
  type ListingItem,
  extractAreaListingsFromQuery,
  extractListingsFromQuery,
  extractRadiusListingsFromQuery,
} from '@modules/_shared/utils/listingDataTransformers';
import {
  CMA_LISTINGS_BY_AREA,
  CMA_LISTINGS_BY_RADIUS,
  COMPARABLE_CMA_LISTINGS,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import { useDebouncedValue } from '@shared/hooks/debounce';

const DEFAULT_RADIUS_METERS = 5000;
const SEARCH_DEBOUNCE_MS = 700;

type AddressInput = {
  route?: string | null;
  street_number?: string | null;
  postcode?: string | null;
  locality?: string | null;
  lat?: number | null;
  lng?: number | null;
};

type UseListingsSearchResult = {
  listings: ListingItem[];
  loading: boolean;
  error: ErrorLike | null;
};

export const useListingsSearch = (
  address: AddressInput | null | undefined,
  countryCode: string,
  agencyFilter: AgencyFilter | null,
  excludedListingIds?: number[],
): UseListingsSearchResult => {
  const debouncedAddress = useDebouncedValue(address, SEARCH_DEBOUNCE_MS);

  const trimmedRoute = debouncedAddress?.route?.trim() ?? '';
  const hasRoute = !!trimmedRoute;
  const hasStreetNumber = !!debouncedAddress?.street_number?.trim();
  const hasPostcode = !!debouncedAddress?.postcode;
  const hasLocality = !!debouncedAddress?.locality;
  const hasCoordinates =
    debouncedAddress?.lat != null && debouncedAddress?.lng != null;

  const { stateId, loading: stateLoading } = useStateByCoordinates(
    debouncedAddress?.lat,
    debouncedAddress?.lng,
    countryCode,
  );

  const addressWhereClause = useMemo(() => {
    if (!trimmedRoute) {
      return undefined;
    }

    return {
      listing: {
        route: { _eq: trimmedRoute },
        offline_since: { _is_null: false },
        ...(debouncedAddress?.street_number && {
          street_number: { _eq: debouncedAddress.street_number },
        }),
        ...(debouncedAddress?.postcode && {
          postcode: { _eq: debouncedAddress.postcode },
        }),
        ...(debouncedAddress?.locality && {
          locality: { _eq: debouncedAddress.locality },
        }),
        ...agencyFilter,
        ...(excludedListingIds?.length && {
          id: { _nin: excludedListingIds },
        }),
      },
    };
  }, [trimmedRoute, debouncedAddress, agencyFilter, excludedListingIds]);

  const radiusListingWhereClause = useMemo(() => {
    const baseFilters: Record<string, unknown> = {
      offline_since: { _is_null: false },
      ...(debouncedAddress?.postcode && {
        postcode: { _eq: debouncedAddress.postcode },
      }),
      ...(excludedListingIds?.length && {
        id: { _nin: excludedListingIds },
      }),
    };

    if (agencyFilter) {
      return { ...baseFilters, ...agencyFilter };
    }

    return baseFilters;
  }, [agencyFilter, debouncedAddress?.postcode, excludedListingIds]);

  const shouldRunExactSearch = hasRoute && hasStreetNumber && !stateLoading;

  const {
    data: addressData,
    loading: addressLoading,
    error: addressError,
  } = useQuery(COMPARABLE_CMA_LISTINGS, {
    context: { clientName: 'scrapers' },
    skip: !shouldRunExactSearch,
    variables: {
      pageSize: 5,
      stateId,
      postcode: debouncedAddress?.postcode,
      locality: debouncedAddress?.locality,
      lat: debouncedAddress?.lat ?? 0,
      lng: debouncedAddress?.lng ?? 0,
      ...buildCountryWhereClause(countryCode, addressWhereClause),
    },
  });

  const addressListings = addressLoading
    ? []
    : extractListingsFromQuery(addressData);

  const exactSearchDone = shouldRunExactSearch && !addressLoading;
  const exactSearchEmpty = exactSearchDone && addressListings.length === 0;
  const isPostcodeOnlySearch = hasPostcode && !hasRoute && !hasStreetNumber;
  const isLocalityOnlySearch =
    hasLocality && !hasPostcode && !hasRoute && !hasStreetNumber;
  const shouldRunRadiusSearch =
    hasRoute && hasCoordinates && (!hasStreetNumber || exactSearchEmpty);
  const shouldRunAreaSearch = isPostcodeOnlySearch || isLocalityOnlySearch;

  const {
    data: radiusData,
    loading: radiusLoading,
    error: radiusError,
  } = useQuery(CMA_LISTINGS_BY_RADIUS, {
    context: { clientName: 'scrapers' },
    skip: !shouldRunRadiusSearch,
    variables: {
      lat: debouncedAddress?.lat ?? 0,
      lng: debouncedAddress?.lng ?? 0,
      radiusMeters: DEFAULT_RADIUS_METERS,
      ...buildRadiusWhereClause(countryCode, radiusListingWhereClause),
    },
  });

  const areaWhereClause = useMemo(() => {
    const clause = { ...radiusListingWhereClause };

    if (isLocalityOnlySearch && debouncedAddress?.locality) {
      clause.locality = { _eq: debouncedAddress.locality };
    }

    return clause;
  }, [
    radiusListingWhereClause,
    isLocalityOnlySearch,
    debouncedAddress?.locality,
  ]);

  const {
    data: areaData,
    loading: areaLoading,
    error: areaError,
  } = useQuery(CMA_LISTINGS_BY_AREA, {
    context: { clientName: 'scrapers' },
    skip: !shouldRunAreaSearch,
    variables: {
      isCh: countryCode === 'CH',
      isFr: countryCode === 'FR',
      isEs: countryCode === 'ES',
      isIt: countryCode === 'IT',
      isPl: countryCode === 'PL',
      isPt: countryCode === 'PT',
      chWhere: areaWhereClause,
      frWhere: areaWhereClause,
      esWhere: areaWhereClause,
      itWhere: areaWhereClause,
      plWhere: areaWhereClause,
      ptWhere: areaWhereClause,
    },
  });

  const radiusListings = useMemo(() => {
    const listings = extractRadiusListingsFromQuery(radiusData);
    const targetRoute = trimmedRoute.toLowerCase();

    if (targetRoute) {
      return listings.sort((a, b) => {
        const aMatch = a.route?.toLowerCase().includes(targetRoute);
        const bMatch = b.route?.toLowerCase().includes(targetRoute);
        if (aMatch && !bMatch) {
          return -1;
        }
        if (!aMatch && bMatch) {
          return 1;
        }
        return 0;
      });
    }

    return [...listings].sort((a, b) => {
      const dateA = a.offline_since ? new Date(a.offline_since).getTime() : 0;
      const dateB = b.offline_since ? new Date(b.offline_since).getTime() : 0;
      return dateB - dateA;
    });
  }, [radiusData, trimmedRoute]);

  const areaListings = useMemo(() => {
    const listings = extractAreaListingsFromQuery(areaData);
    return [...listings].sort((a, b) => {
      const dateA = a.offline_since ? new Date(a.offline_since).getTime() : 0;
      const dateB = b.offline_since ? new Date(b.offline_since).getTime() : 0;
      return dateB - dateA;
    });
  }, [areaData]);

  const listings =
    addressListings.length > 0
      ? addressListings
      : shouldRunAreaSearch
      ? areaListings
      : radiusListings;

  const isDebouncing = address !== debouncedAddress;
  const willRunSearch =
    shouldRunExactSearch || shouldRunRadiusSearch || shouldRunAreaSearch;
  const loading =
    (isDebouncing && willRunSearch) ||
    stateLoading ||
    (shouldRunExactSearch && addressLoading) ||
    (shouldRunRadiusSearch && radiusLoading) ||
    (shouldRunAreaSearch && areaLoading);

  const error = addressError ?? radiusError ?? areaError ?? null;

  return {
    listings,
    loading,
    error,
  };
};
